"use client";

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * What a page may put in the side nav:
 *
 *   • `topAction` — a control above the panel's links (the admin area's Build
 *     content launcher).
 *   • `outline`   — the page's own table of contents, shown in the panel
 *     instead of the route's menu while the page is open: `{ title, links,
 *     activeHrefs }`, with `links` in the manifest's item vocabulary (`label`,
 *     `href`, `icon` or `iconName`, `subItems`, `count`) and `activeHrefs`
 *     from the page's scroll spy. The playbook registers one.
 *   • `coach`     — Sabine's chat about this page: `{ placeholder, chat,
 *     defaultOpen?, open?, onOpenChange? }`. The nav shows the Ask button above
 *     the page with `placeholder` as its question, and opens `chat` (an
 *     `<AIChat>` element) in a column beside the rail, in the menu panel's
 *     place. Leave `open` out and the nav owns whether it is open — closed,
 *     or open from the start with `defaultOpen` (the home page); pass `open`
 *     and `onOpenChange` when the page also opens it itself (a one-click
 *     coaching action). The element renders in the nav's tree, so it reads
 *     the nav's providers, not the page's: hand it page context as props.
 *
 * The provider sits in SideNav; a page registers with <SideNavSlot>. Registering
 * is a layout effect, so it clears when the page unmounts, the registrar
 * renders nothing of its own, and the nav renders the new value in the same
 * commit as the page. That matters for the coach: its composer is a
 * controlled field whose value the page holds, and a value that reached the
 * nav one commit late was snapped back by React, so fast typing lost every
 * other key. A changed value replaces the old one in place, never passing
 * through empty, so a coach whose thread streams in updates the nav once per
 * chunk and keeps the pane open.
 */

const EMPTY = Object.freeze({ topAction: null, outline: null, coach: null });
const SlotsContext = createContext(EMPTY);
const SetSlotsContext = createContext(null);

export function SideNavSlotsProvider({ children }) {
  const [slots, setSlots] = useState(EMPTY);
  return (
    <SetSlotsContext.Provider value={setSlots}>
      <SlotsContext.Provider value={slots}>{children}</SlotsContext.Provider>
    </SetSlotsContext.Provider>
  );
}

export function useSideNavSlots() {
  return useContext(SlotsContext);
}

const emptySlots = (keys) => Object.fromEntries(keys.map((key) => [key, null]));

/** Put something in the nav for as long as this is mounted. */
export function SideNavSlot({ topAction, outline, coach }) {
  // Only the setter is read here, so a slot change never re-renders the
  // registrar — which is what keeps a page that rebuilds its outline on every
  // render from registering in a loop.
  const setSlots = useContext(SetSlotsContext);
  // The slots this registrar filled, so a slot it stops passing is cleared
  // and unmounting clears exactly these.
  const filledRef = useRef([]);

  useLayoutEffect(() => {
    if (!setSlots) return;
    const provided = {};
    if (topAction !== undefined) provided.topAction = topAction;
    if (outline !== undefined) provided.outline = outline;
    if (coach !== undefined) provided.coach = coach;

    const dropped = filledRef.current.filter((key) => !(key in provided));
    filledRef.current = Object.keys(provided);
    setSlots((prev) => ({ ...prev, ...emptySlots(dropped), ...provided }));
  }, [setSlots, topAction, outline, coach]);

  // Cleared in the same phase it registers in, so a page mounting in the same
  // commit can never register before the page it replaces has cleared.
  useLayoutEffect(() => {
    if (!setSlots) return undefined;
    return () => setSlots((prev) => ({ ...prev, ...emptySlots(filledRef.current) }));
  }, [setSlots]);

  return null;
}
